// Built-in
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { Button, Text, SimpleText, DarkTooltip, Toast, Flexbox } from '@morressier/ts';

// External
import { Grid, useMediaQuery, useTheme } from '@material-ui/core';
import { FaCompressArrowsAlt, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import useSWR from 'swr';

// Internal
import { VIDEO_API_URL, standaloneFetcher } from 'api/livestream-rtmp';
import {
  fetchSessionPresentations,
  fetchSessionSpeakers,
  updateToJoinStageRequest,
} from 'api/session';
import { BlockedComponentType } from 'components/EventStandardContainer';
import PosterGrid from 'components/PosterGrid';
import Ratio from 'components/Ratio';
import Speakers from 'components/Speakers';
import { AdvanceToolTip } from 'components/ToolTip';
import { canDisplayVideoCutterPlayer } from 'components/VideoPlayer/helper';
import { FeatureFlags } from 'config/featureFlags';
import { useSessionsBookmarks } from 'containers/SessionsList/hooks';
import { BookmarkButton } from 'containers/SessionsList/SessionsListItem';
import { BlackVideoCard, SimpleCard } from 'containers/SessionsList/styles';
import { useAuthedUser } from 'hooks/useIsAuthedUser';
import { useLocation } from 'hooks/useLocation';
import { useMouseDrag } from 'hooks/useMouseDrag';
import { SingleSessionPageProps } from 'pages/event/[eventId]/sessions/[sessionId]';
import { useDocumentVideo } from 'services/documents';
import { useAuthContext, useEventContext } from 'store';
import { useRegAndTicketingContext } from 'store/modules/eventTicketing';
import { checkIsAsce, checkPaywal } from 'utils/checkPaywall';
import { logEvent } from 'utils/eventTracking';
import { logger } from 'utils/logger';
import { scrollElementIntoView } from 'utils/scrollElementIntoVIew';

// Relative Path
import { useFeatureFlagContext } from 'utils/unleashFeatureFlags/FeatureFlagProvider';
import { FEATURE_FLAGS } from 'utils/unleashFeatureFlags/flags';

import * as ContainerStyles from '../styles';
import CalendarExportDropdown from './CalendarExportDropdown';
import { CommentsContainer } from './CommentsContainer';
import { useLiveSessionContext } from './context/LiveSessionContext';
import { HeaderSection } from './HeaderSection';
import { getSpeakersFromPoster, isElementInViewport, isVideoPlaying } from './helpers';
import { useSessionFetch, useSessionTime, useGetUserMedia, useSessionAds, laggy } from './hooks';
import { DelegateAcceptModal } from './Modal/DelegateAcceptModal';
import { DelegateRejectModal } from './Modal/DelegateRejectModal';
import { RegOrBuyTicket } from './Modal/RegOrBuyTicketRequired';
import { PaginationControls } from './PaginationControls';
import { Playlist } from './Playlist';
import Presenter from './Presenter';
import RTMPRender from './Rtmp/RTMPRender';
import { SessionComments } from './Rtmp/SessionComments';
import { SignInScreen } from './Screens/SignInScreen';
import * as Styles from './styles';
import Tags from './Tags';
import Video from './Video';

const LiveStreamHLS = dynamic(import('./LiveStreamHLS'), { ssr: false });
const LiveStream = dynamic(import('./LiveStream'), { ssr: false });

const topBarOffset = 64;
const delegateRequestAcceptTimeThreshold = 40; // seconds
const presentationsPerPage = 10;
const presentationsPaginationThreshold = 100;
const speakersPerPage = 10;
const speakersPaginationThreshold = 100;

export const SingleSessionContainer: React.FC<SingleSessionPageProps['data']> &
  BlockedComponentType = props => {
  const {
    presentersCount: { count: presentersCount },
    videoSession,
    presentationsCount: { count: presentationsCount },
  } = props;
  const origin = useLocation()?.origin;
  const { state: event } = useEventContext();
  const { state: authState } = useAuthContext();
  const { query, push } = useRouter();

  const eventId = props.session.event?.id || (query.eventId as string) || '';
  const sessionId = (query.sessionId as string) || '';
  const userEvents = authState?.userEvents || [];
  const posterId = query['poster-id'] as string | undefined;
  const backStageLink = `${origin}/session/${sessionId}/backstage`;
  const stageLink = `${origin}/session/${sessionId}/live`;
  const params = useMemo(() => ({ include: 'ads' }), []);

  const [postersPage, setPostersPage] = useState(1);
  const [speakersPage, setSpeakersPage] = useState(1);
  const paginatePresentations = presentationsCount > presentationsPaginationThreshold;
  const totalPages = presentationsCount ? Math.ceil(presentationsCount / presentationsPerPage) : 1;
  const paginateSpeakers = presentersCount > speakersPaginationThreshold;
  const { features } = useFeatureFlagContext();
  const authUser = authState?.profile;
  const userId = authUser?.id;

  const { data: session, refetch: refetchSession } = useSessionFetch(
    eventId,
    sessionId,
    props.session,
    props.useDummyData,
    params,
  );
  const {
    bookmarks = {},
    toggleBookmark,
    isToggling,
    isLoading,
  } = useSessionsBookmarks(!!userId, eventId, session._id, userId);

  const { data: presentations } = useSWR(
    `${eventId}/sessions/${sessionId}/presentations${
      paginatePresentations ? `?page=${postersPage}` : ''
    }`,
    () =>
      fetchSessionPresentations(eventId, sessionId, {
        paginate: paginatePresentations,
        page: postersPage,
        itemsPerPage: presentationsPerPage,
      }),
    { use: [laggy] },
  );

  const { data: presenters } = useSWR(
    `${eventId}/sessions/${sessionId}/speakers${paginateSpeakers ? `?page=${speakersPage}` : ''}`,
    () =>
      fetchSessionSpeakers(eventId, sessionId, {
        paginate: paginateSpeakers,
        page: speakersPage,
        itemsPerPage: speakersPerPage,
      }),
    { use: [laggy] },
  );

  const { liveStatus } = useLiveSessionContext();

  const [canRequestJoinBackStage, setCanRequestJoinBackStage] = useState<boolean>(false);
  const [isRequestedToJoin, setIsRequestedToJoin] = useState<boolean>(false);
  const [showRejectRequestModal, setShowRejectRequestModal] = useState<boolean>(false);
  const [showRequestAcceptModal, setShowRequestAcceptModal] = useState<boolean>(false);
  const [hostName, setHostName] = useState<string>('');
  const [isSendingJoinRequest, setIsSendingJoinRequest] = useState<boolean>(false);
  const [joinRequest, _setJoinRequest] = useState<JoinRequest>();
  const [timeLeftToAccept, setTimeLeftToAccept] = useState<number>(
    delegateRequestAcceptTimeThreshold,
  );
  const joinStatusRef = useRef(joinRequest);
  const [presentationStarted, setPresentationStarted] = useState(false);
  const [playing, setPlaying] = useState<number | null>(
    liveStatus === 'ENDED' || Boolean(session.rtmp_stream?.stream_ended_at) ? 0 : null,
  );

  const isOrganizer = userEvents?.some(ue => ue.id === eventId && ue.roles.includes('organizer'));
  const isChair =
    !!authUser?.id &&
    !!session.chairs?.length &&
    session.chairs.some(chair => chair?._id === authUser?.id);

  /**
   / =================================================================================================
   * Alternative implementations. Need to compare these later
   const isChair = userEvents?.some(ue => ue.id === eventId && ue.roles.includes('chair'));
   const isSpeaker = userEvents?.some(ue => ue.id === eventId && ue.roles.includes('presenter'));
   */
  // =================================================================================================

  const setJoinRequest = (x: JoinRequest) => {
    joinStatusRef.current = x;
    _setJoinRequest(x);
  };

  const sessionTime = useSessionTime({
    startDate: session.start_date,
    endDate: session.end_date,
    options: { runPastDates: true },
  });

  const { devicesPermissionState } = useGetUserMedia();
  const [isBrowserCheckTooltipVisible, setIsBrowserCheckTooltipVisible] = useState(false);
  const { state, processAction } = useRegAndTicketingContext();
  const isTicketingFlagEnabled = FeatureFlags.Event_Ticketing.isEnabled();

  const { isPreSession, isPostSession, isHappeningNow } = sessionTime;

  const isRtmpStream = session.stream_type === 'RTMP_STREAM';
  const shouldShowVideoModeForRtmp = Boolean(
    session.rtmp_stream?.stream_ended_at &&
      videoSession?.presentations?.some(p => p.is_assigned) &&
      isRtmpStream,
  );

  const floatingVideoRef = useRef<HTMLDivElement>(null);

  // when using LiveStream, OTSubscriber adds a getSubscriber to the ref
  const videoRef = useRef<HTMLVideoElement & { getSubscriber?: unknown }>(null);
  const [floatVideo, setFloatVideo] = useState(false);
  const [disableFloat, setDisableFloat] = useState(false);

  const floatingElRef = useRef<HTMLDivElement>(null);

  const { isDragging, resetDragPositions } = useMouseDrag(floatingElRef.current, {
    keepOldPosition: true,
    enableDrag: floatVideo,
  });

  const { showAd, RenderAd } = useSessionAds({
    session,
    liveStatus,
    isEnded: shouldShowVideoModeForRtmp,
  });

  // Work around until we implement end live stream on main stage properly
  const optimisticHasLiveStreamEnded =
    shouldShowVideoModeForRtmp ||
    (session.mode === 'Live' &&
      isPostSession &&
      (liveStatus === 'ENDED' || liveStatus === 'PAUSED') &&
      !presentationStarted);

  const getHasEnded = useCallback(
    (id: string) =>
      session.mode === 'Live' &&
      session.main_stage?.presentations?.find(p => p.posterId === id)?.end,
    [session.mode, session.main_stage?.presentations],
  );

  const [playlistPosters, setPlaylistPosters] = useState(() =>
    session.posters.map(poster => ({ ...poster, hasEnded: !!getHasEnded(poster.id) })),
  );

  /**
   * An optimistic way of knowing when all presentations have been presented.
   * Also just another optimisticHasLiveStreamEnded
   */
  const isAllPresented = () =>
    playlistPosters.every(p => p.hasEnded === true) && liveStatus === 'PAUSED';

  const isMainStageLiveOrPaused = liveStatus === 'LIVE' || liveStatus === 'PAUSED';

  const [livePosterId, setLivePosterId] = useState<string | null>();
  useEffect(() => {
    setLivePosterId(
      session.main_stage?.presentations.find(poster => poster.end === undefined)?.posterId,
    );
  }, [session.main_stage?.presentations]);
  const newIndexPlaying = session.posters.findIndex(poster => poster._id === livePosterId);

  const isAuthed = useAuthedUser();

  const getIsAuthOrAccessRequired = () => {
    if (isTicketingFlagEnabled && state.isTicketedEvent) {
      return !state.userHasAccess && !!(isHappeningNow || isMainStageLiveOrPaused);
    }

    return !isAuthed;
  };

  const getCurrentPoster = () => {
    const lastPlayedPosterId = session.main_stage?.presentations
      .filter(p => !!p.end)
      .sort((a, b) => new Date(b.end!).getTime() - new Date(a.end!).getTime())[0]?.posterId;

    if (isMainStageLiveOrPaused && livePosterId) {
      const newIndex = session.posters.findIndex(poster => poster._id === livePosterId);

      setPresentationStarted(true);
      setPlaying(newIndex);
      return session.posters[newIndex];
    }

    if (session.mode === 'Video') {
      setPlaying(0);
      return session.posters[0];
    }

    if (liveStatus === 'PAUSED' && lastPlayedPosterId && !isPostSession) {
      const newIndex = session.posters.findIndex(poster => poster._id === lastPlayedPosterId);

      return session.posters[newIndex];
    }

    return undefined;
  };

  const [currentPoster, setCurrentPoster] = useState(() => getCurrentPoster());
  const isPosterAuthor = currentPoster?.authors.some(a => a.id === authUser?.id);
  const currentPresenter = getSpeakersFromPoster(currentPoster)[0];
  const organizationId = event?.organization_id?._id;
  const canShowVideo = event?.embargoed ? isAuthed : true;
  const { data, isLoading: isCheckingPaywallAccess } = useDocumentVideo({
    organization_id: organizationId,
    event_id: session.event.id,
    article_id: currentPoster?.id,
    canShowVideo,
  });

  useEffect(() => {
    if (isMainStageLiveOrPaused && livePosterId && newIndexPlaying >= 0) {
      setPlaying(newIndexPlaying);
      setCurrentPoster(session.posters[newIndexPlaying]);
      setPresentationStarted(true);
      setPlaylistPosters(
        session.posters.map(poster => ({ ...poster, hasEnded: !!getHasEnded(poster.id) })),
      );
    }
  }, [isMainStageLiveOrPaused, livePosterId, newIndexPlaying, getHasEnded, session.posters]);

  useEffect(() => {
    if (isMainStageLiveOrPaused && !livePosterId && playing !== null) {
      setPlaying(null);
      setCurrentPoster(undefined);
      setPresentationStarted(false);
      setPlaylistPosters(
        session.posters.map(poster => ({ ...poster, hasEnded: !!getHasEnded(poster.id) })),
      );
    }
  }, [isMainStageLiveOrPaused, livePosterId, playing, getHasEnded, session.posters]);

  // Updates current poster because useState takes old data and we need to keep this state to latest state after swr has fetched
  useEffect(() => {
    if (liveStatus === 'ENDED' || optimisticHasLiveStreamEnded || session.mode === 'Video') {
      setCurrentPoster(session.posters[0]);
    }
  }, [session.posters, liveStatus, optimisticHasLiveStreamEnded, session.mode]);
  // Updates playlist posters because useState takes old data and we need to keep this state to latest state after swr has fetched
  useEffect(() => {
    setPlaylistPosters(
      session.posters.map(poster => ({ ...poster, hasEnded: !!getHasEnded(poster.id) })),
    );
  }, [getHasEnded, session.posters]);

  // the only case for hidden comments is livestream ON but poster unselected
  const hideComments = isMainStageLiveOrPaused && !currentPoster;

  useEffect(() => {
    // we need to update the currentPoster when either playing or posterId from url query changes
    if (posterId) {
      if (presentationStarted) return;

      session.posters.forEach((poster, i) => {
        if (poster.id === posterId) {
          setCurrentPoster(poster);

          if (liveStatus === 'LIVE') {
            setPlaying(null);
          } else {
            setPlaying(i);
          }
        }
      });
    }
  }, [posterId, presentationStarted, session.posters, liveStatus]);

  useEffect(() => {
    if (session.main_stage && session.main_stage.delegate_join_requests) {
      const currentUserJoinRequest = session.main_stage.delegate_join_requests.find(
        request => request.user === authUser?.id,
      );
      setIsRequestedToJoin(currentUserJoinRequest?.status === 'REQUESTED');
      const hostUpdatedRequest = session.main_stage.delegate_join_requests.find(
        request =>
          request.user === authUser?.id &&
          (request.status === 'ACCEPTED' || request.status === 'REJECTED'),
      );

      if (currentUserJoinRequest) {
        setJoinRequest(currentUserJoinRequest);
      }

      if (hostUpdatedRequest) {
        const { status, updated_by, updated_at } = hostUpdatedRequest;
        if (status === 'ACCEPTED') {
          setHostName(updated_by.full_name);
          const acceptedTime = Date.parse(updated_at) / 1000;
          const currentTime = Date.now() / 1000;
          const timeGap = currentTime - acceptedTime;
          setTimeLeftToAccept(Math.floor(delegateRequestAcceptTimeThreshold - timeGap));
          setShowRequestAcceptModal(timeGap <= delegateRequestAcceptTimeThreshold);
        }
      }

      if (currentUserJoinRequest?.status === 'REQUESTED') {
        toast(<Toast title="Your request to Join the Stage has been sent." />, {
          autoClose: 3000,
        });
      }
    }
    setCanRequestJoinBackStage(session.main_stage?.is_allowed_to_delegate);
  }, [session.main_stage, authUser]);

  useEffect(() => {
    if (floatVideo === false) {
      resetDragPositions();
    }
  }, [floatVideo, resetDragPositions]);

  useEffect(() => {
    const onPageScroll = async () => {
      if (floatingVideoRef.current) {
        const isInView = isElementInViewport(floatingVideoRef.current, topBarOffset);

        if (isInView) {
          setFloatVideo(false);
          setDisableFloat(false);
        }

        if (!isInView && videoRef.current) {
          if (window.pageYOffset + 100 < floatingVideoRef.current.offsetTop || disableFloat) {
            return setFloatVideo(false);
          }

          if (
            (videoRef.current?.getSubscriber || session.event.live_stream_mode === 'INTERACTIVE') &&
            isMainStageLiveOrPaused
          ) {
            return setFloatVideo(true);
          }

          return isVideoPlaying(videoRef.current) && setFloatVideo(true);
        }
      }

      return () => {
        // DO something here??
      };
    };

    window.addEventListener('scroll', onPageScroll);

    return () => window.removeEventListener('scroll', onPageScroll);
  }, [disableFloat, isMainStageLiveOrPaused, session.event.live_stream_mode]);

  useEffect(() => {
    logEvent('STANDALONE_VISITED_SESSION_PAGE', {
      session_id: session._id,
      is_hybrid_session: isRtmpStream,
      mode: session.mode,
    });
  }, [session._id, session.mode, isRtmpStream]);

  // Workaround to update playlistPoster hasEnded status
  const updatePlaylistPoster = useCallback((pId: string, hasEnded = true) => {
    setPlaylistPosters(prev =>
      prev.map(poster => {
        if (poster.id === pId) {
          return {
            ...poster,
            hasEnded,
          };
        }

        return poster;
      }),
    );
  }, []);

  const shouldShowQnA = () =>
    event?.type !== 'Onsite' &&
    (!isPreSession || liveStatus === 'LIVE') &&
    !isRtmpStream &&
    !hideComments &&
    (isTicketingFlagEnabled ? true : !!authUser?.id) &&
    !!currentPoster;

  // This needs to keep the same reference
  const handleLiveSignal = useCallback(
    (signal: LiveSignal) => {
      // TODO: Remove this after session view is properly tested and released
      logger.log(signal);

      if (signal.event === 'PRESENTATION_STARTED') {
        const newIndex = session.posters.findIndex(
          poster => poster._id === signal.payload.posterId,
        );

        if (newIndex >= 0) {
          const poster = session.posters[newIndex];

          setCurrentPoster(prev => {
            if (prev) {
              // get the previous then end it in playlist poster
              updatePlaylistPoster(prev?._id);
            }
            return poster;
          });
          setPlaying(newIndex);
          setPresentationStarted(true);
          setLivePosterId(poster._id);
        }
      }

      if (signal.event === 'PRESENTATION_ENDED') {
        refetchSession();
        setPresentationStarted(false);
        setPlaying(null);
        setLivePosterId(null);
        setCurrentPoster(prev => {
          if (prev) {
            // get the previous then end it in playlist poster
            updatePlaylistPoster(prev?._id);
          }
          return undefined;
        });
      }
      if (signal.event === 'TOGGLE_JOIN_STATUS') {
        setCanRequestJoinBackStage(signal.payload.status);
      }
      if (signal.event === 'DELEGATE_JOIN_REQUEST_ACCEPTED') {
        if (signal.payload.delegateUserId === authUser?.id) {
          setHostName(signal.payload.senderName);
          setTimeLeftToAccept(delegateRequestAcceptTimeThreshold);
          setShowRequestAcceptModal(true);
          setJoinRequest({ ...(joinRequest as JoinRequest), status: 'ACCEPTED' });
        }
      }
      if (signal.event === 'DELEGATE_JOIN_REQUEST_REJECTED') {
        if (
          signal.payload.delegateUserId === authUser?.id ||
          (signal.payload?.rejectAll === true && joinStatusRef?.current?.status === 'REQUESTED')
        ) {
          setJoinRequest({ ...(joinRequest as JoinRequest), status: 'REJECTED' });

          setShowRejectRequestModal(!signal.payload.forcedCancel);
          setShowRequestAcceptModal(false);
          setIsRequestedToJoin(false);
        }
      }
    },
    [authUser?.id, session.posters, updatePlaylistPoster],
  );

  const discussionsElement = useRef<HTMLDivElement>(null);

  const { breakpoints } = useTheme();
  const isExtraSmallDevice = useMediaQuery(breakpoints.down('xs'));
  const isLargeDevice = useMediaQuery(breakpoints.up('lg'));

  const [focusMainEditor, setFocusMainEditor] = useState(false);
  const { data: rtmpStream } = useSWR<RtmpStream, Error>(
    `${VIDEO_API_URL}/${sessionId}?isOrganizer=true`,
    standaloneFetcher,
  );

  const scrollToDiscussions = async () => {
    setFocusMainEditor(false);

    if (discussionsElement.current) {
      scrollElementIntoView(
        discussionsElement.current,
        {
          behavior: 'smooth',
          block: 'center',
        },
        { top: topBarOffset },
      ).then(r => {
        if (r.isInView) {
          setFocusMainEditor(true);
        }
      });
    }
  };

  const onClickRequestToJoinBackstage = async (status: string) => {
    const delegateId = authUser?.id;
    if (!delegateId) {
      toast(<Toast title="Please sign in to Join the Stage." />);
    } else {
      try {
        setIsSendingJoinRequest(true);
        await updateToJoinStageRequest(eventId, sessionId, delegateId, status);
        setIsRequestedToJoin(status === 'REQUESTED');
        setJoinRequest({ ...(joinRequest as JoinRequest), status: 'REQUESTED' });
        setIsSendingJoinRequest(false);
        toast(
          <Toast
            title={
              status === 'CANCELED'
                ? 'Your request to Join the Stage has been cancelled.'
                : 'Your request to Join the Stage has been sent.'
            }
          />,
          { autoClose: 3000 },
        );
      } catch (e) {
        setIsSendingJoinRequest(false);
        toast(<Toast title="Something went wrong, Please try again." />);
      }
    }
  };

  const onClickDeclineToJoinBackstage = async () => {
    const delegateId = authUser?.id;
    if (!delegateId) {
      toast(<Toast title="Please sign in to Join the Stage." />);
    } else {
      try {
        logEvent('JOIN_STAGE_INVITATION_NOT_NOW', { event_id: eventId, session_id: sessionId });
        await updateToJoinStageRequest(eventId, sessionId, delegateId, 'DECLINED');
        setJoinRequest({ ...(joinRequest as JoinRequest), status: 'DECLINED' });
        setIsRequestedToJoin(false);
      } catch (e) {
        setIsSendingJoinRequest(false);
        toast(<Toast title="Something went wrong, Please try again." />);
      }
    }
  };

  const onClickAcceptToJoinBackstage = async () => {
    const delegateId = authUser?.id;
    if (!delegateId) {
      toast(<Toast title="Please sign in to Join the Stage." />);
    } else {
      try {
        logEvent('JOIN_STAGE_INVITATION_ACCEPTED', { event_id: eventId, session_id: sessionId });

        window.location.href = stageLink;
      } catch (e) {
        setIsSendingJoinRequest(false);
        toast(<Toast title="Something went wrong, Please try again." />);
      }
    }
  };

  const isSessionVOD = session.mode === 'Video';
  const isSessionVODReady =
    session.mode === 'Video' && currentPoster && currentPoster?.video_presentation?.playback_url;
  const shouldShowVideoPlayer =
    event?.type !== 'Onsite' && isRtmpStream
      ? (isPreSession && !!rtmpStream && rtmpStream?.mux_stream_status !== 'idle') || !isPreSession
      : (liveStatus === 'EARLY_START' && showAd) ||
        (!isAuthed && showAd) ||
        ['LIVE', 'PAUSED', 'ENDED'].includes(liveStatus) ||
        isSessionVOD ||
        isHappeningNow ||
        isPostSession;
  const shouldShowPlaylist =
    event?.type !== 'Onsite' && isRtmpStream
      ? shouldShowVideoModeForRtmp
      : !isPreSession ||
        !['NOT_STARTED', 'EARLY_START'].includes(liveStatus) ||
        event?.type !== 'Onsite';
  const shouldShowVideoModeForLiveSession = canDisplayVideoCutterPlayer(
    videoSession,
    currentPoster?.id,
  );
  const shouldHideSessionContainer =
    presentations?.length === 0 && !shouldShowVideoPlayer && !shouldShowPlaylist;

  const logRecorderVideoView = () => {
    logEvent('VIEWED_VIDEO_PRESENTATION', {
      id: currentPoster?.id,
      video_number: playing,
      session_id: session._id,
    });
  };

  const logWatchTime = (args: { watchedFor: number }) => {
    logEvent('WATCHED_VIDEO_PRESENTATION', {
      id: currentPoster?.id,
      video_number: playing,
      session_id: session._id,
      watched_for: args.watchedFor,
    });
  };

  const isPlaylistBlocked = () => {
    if (session.mode === 'Video') return false;

    return !optimisticHasLiveStreamEnded ?? (isMainStageLiveOrPaused && session.mode === 'Live');
  };

  const hasPlayableAssignedVideos = (posters: typeof session['posters']) =>
    posters.some(p => canDisplayVideoCutterPlayer(videoSession, p.id));

  // TODO: Implement listener to know when recordings are available in real time??
  const isRecordingAvailable = () => {
    if (session.mode === 'Live') {
      return (
        (session.main_stage?.archive_status === 'available' ||
          session.main_stage?.archive_status === 'uploaded') &&
        !!hasPlayableAssignedVideos(session.posters)
      );
    }

    return session?.posters?.some(poster => poster.video_presentation?.playback_url);
  };

  const hasRecordings = isRecordingAvailable() && session.main_stage?.live_status !== 'PAUSED';
  const blockPlaylist = isPlaylistBlocked();

  const showSubmissions = event?.tabsConfig?.submissions?.isEnabled;

  const isBookmarked = bookmarks[session._id]?.isBookmarked;
  const canShowBookmarkButton = isTicketingFlagEnabled ? true : !!userId && !isLoading;

  const SubmissionsTitle = () =>
    presentations && presentations.length > 0 ? (
      <ContainerStyles.SubTitle>Presentations</ContainerStyles.SubTitle>
    ) : null;

  const isAsce = checkIsAsce(organizationId);
  const hasPaywall = checkPaywal(organizationId);
  const showSubscribe = !isCheckingPaywallAccess && !data?.signed_url && hasPaywall;
  const isPresenter = presenters?.some(presenter => presenter?._id === authUser?.id);
  const renderVideoOrStream = () => {
    if (isTicketingFlagEnabled && (getIsAuthOrAccessRequired() || state.isLoadingAccess))
      return <RegOrBuyTicket />;

    if (
      !shouldShowVideoModeForLiveSession &&
      !isSessionVOD &&
      !isRtmpStream &&
      event?.type !== 'Onsite'
    ) {
      if (authUser) {
        return session.event.live_stream_mode === 'HLS' ? (
          <LiveStreamHLS
            userId={authUser.id}
            morressierSessionInfo={session}
            handleSignal={handleLiveSignal}
            isViewer={!(isOrganizer || isPresenter)}
            logRecorderVideoView={logRecorderVideoView}
            forwardRef={videoRef}
            renderAd={showAd ? () => <RenderAd /> : undefined}
          />
        ) : (
          <LiveStream
            userId={authUser.id}
            morressierSessionInfo={session}
            onSignal={handleLiveSignal}
            isOrganizer={isOrganizer}
            isPresenter={isPresenter}
            forwardRef={videoRef}
            hasLiveStreamEnded={optimisticHasLiveStreamEnded}
            currentPresentingPosterId={currentPoster?.id}
            isAllPresented={isAllPresented()}
            renderAd={showAd ? () => <RenderAd /> : undefined}
          />
        );
      }

      return <SignInScreen />;
    }

    if (showAd) return <RenderAd />;

    if (currentPoster && isAsce && showSubscribe && !isCheckingPaywallAccess) {
      return (
        <div
          style={{
            position: 'relative',
            width: '100%',
            background: 'rgba(0,0,0,1)',
          }}
        >
          <div
            style={{
              minHeight: '400px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <h3
              style={{
                color: '#FFF',
                textAlign: 'center',
                margin: '16px 48px',
                fontSize: '1.25rem',
                lineHeight: '1.75rem',
              }}
            >
              This video can not be played without ASCE's subscription. Check subscription options
              by clicking the link below.
            </h3>
            {isAsce && (
              <a
                rel="noreferrer"
                target="_blank"
                href="https://ascelibrary.org/page/subscribetoascelibrarypackages"
              >
                <a
                  style={{
                    fontWeight: 600,
                    fontSize: '1.5rem',
                    lineHeight: '1.75rem',
                    color: '#FFF',
                  }}
                >
                  How to buy
                </a>
              </a>
            )}
          </div>
        </div>
      );
    }

    if (currentPoster && (!showSubscribe || !isAsce)) {
      if (shouldShowVideoModeForLiveSession || shouldShowVideoModeForRtmp) {
        if (
          !canDisplayVideoCutterPlayer(videoSession, currentPoster.id) &&
          !currentPoster.video_presentation?.playback_url
        ) {
          return (
            <BlackVideoCard>
              <Text color="white" fontWeight="semiBold">
                This video is unavailable.
              </Text>
            </BlackVideoCard>
          );
        }
        return (
          <Video
            authUserId={authUser?.id}
            poster={currentPoster}
            videoSession={videoSession}
            onPlay={logRecorderVideoView}
            logWatchTime={logWatchTime}
            ref={videoRef}
            containerStyles={{
              paddingTop: 'unset',
              width: '100%',
              height: '100%',
            }}
            pictureInPictureToggle={false}
            archiveStreamUrl={session.main_stage?.current_archive_playback_url}
          />
        );
      }

      if (isSessionVODReady) {
        return (
          <Video
            authUserId={authUser?.id}
            poster={currentPoster}
            videoSession={videoSession}
            onPlay={logRecorderVideoView}
            logWatchTime={logWatchTime}
            ref={videoRef}
            containerStyles={{
              paddingTop: 'unset',
              width: '100%',
              height: '100%',
            }}
            pictureInPictureToggle={false}
            archiveStreamUrl={session.main_stage?.current_archive_playback_url}
          />
        );
      }
    }

    if (isRtmpStream && event?.type !== 'Onsite') {
      return <RTMPRender session={session} authUserId={authUser?.id} />;
    }

    return (
      <SimpleCard color="#1F222C" height="100%" justifyContent="center" alignItems="center">
        <Styles.EmptyVideoIcon size="2rem" color="white" />
        <Text size="body1_new" as="h2" color="white">
          No video is available
        </Text>
        <Text size="body1_new" as="div" color="white">
          Visit the{' '}
          <Link href={`/event/${eventId}/article/${currentPoster?.id}`} passHref>
            <SimpleText color="white" as="a">
              full submission
            </SimpleText>
          </Link>
        </Text>
      </SimpleCard>
    );
  };

  const QnAComponent = shouldShowVideoModeForRtmp ? (
    <SessionComments
      sessionId={session._id}
      authUser={authUser}
      canApprove={isOrganizer || isChair}
      auto_approve_discussions={session.event.auto_approve_discussions}
      isHappeningNow={isHappeningNow || isMainStageLiveOrPaused}
    />
  ) : (
    shouldShowQnA() &&
    currentPoster && (
      <>
        <div style={{ height: !isLargeDevice ? 0 : '0.5rem' }} ref={discussionsElement} />
        <CommentsContainer
          sessionId={session._id}
          posterId={currentPoster.id}
          authUser={authUser}
          canApprove={isOrganizer || isPosterAuthor || isChair}
          auto_approve_discussions={session.event.auto_approve_discussions}
          presenterId={currentPoster?.presentations?.presenters[0]?.id}
          isMainEditorFocused={focusMainEditor}
          authors={currentPoster?.authors || []}
          speakers={currentPoster.presentations?.presenters}
          isRtmpStream={isRtmpStream}
          isHappeningNow={isHappeningNow || (liveStatus && isMainStageLiveOrPaused)}
        />
        <div style={{ height: '2.5rem' }} />
      </>
    )
  );

  const SpeakersAndSubmissions = presentations &&
    !!presenters?.length &&
    presentations.length > 0 && (
      <>
        {!currentPoster && isLargeDevice && shouldShowVideoPlayer && (
          <div style={{ height: '2rem' }} />
        )}
        <Speakers
          event={event as MorressierEvent}
          data={{ count: presentersCount, speakers: presenters }}
          hideTotalSpeakersMessage
          hideShowAll
          customGridProps={{ xs: 12, md: 6 }}
          alwaysHorizontal
          currentPage={speakersPage}
          showPagination={paginateSpeakers}
          speakersPerPage={speakersPerPage}
          onNewPage={setSpeakersPage}
        />
        {showSubmissions && (
          <>
            <Flexbox
              alignItems="center"
              justifyContent="space-between"
              style={{ marginBottom: '0.5rem' }}
            >
              <SubmissionsTitle />
              {paginatePresentations ? (
                <div style={{ marginBottom: '0.5rem' }}>
                  <PaginationControls
                    currentPage={postersPage}
                    totalPages={totalPages}
                    onPreviousPage={setPostersPage}
                    onNextPage={setPostersPage}
                  />
                </div>
              ) : null}
            </Flexbox>
            {presentations ? (
              <PosterGrid
                items={presentations}
                event={eventId}
                distribution="list"
                submissionType="PRESENTATION"
              />
            ) : null}
          </>
        )}
      </>
    );

  const renderJoinRequestButtons = () => {
    const JOIN_EVENT_NAME = !isRequestedToJoin
      ? 'REQUEST_TO_JOIN_STAGE_CLICKED'
      : 'REQUEST_TO_JOIN_STAGE_CANCELED';
    const attributes = {
      session_id: sessionId,
      event_id: eventId,
    };

    if (isRequestedToJoin) {
      return (
        <Button
          style={{ marginLeft: '1rem', border: '1px solid #d9dbdf' }}
          onClick={() => {
            logEvent(JOIN_EVENT_NAME, attributes);
            onClickRequestToJoinBackstage('CANCELED');
          }}
          iconLeft={isSendingJoinRequest ? <FaSpinner /> : ''}
          fullWidth={!!isExtraSmallDevice}
        >
          Cancel Request
        </Button>
      );
    }

    if (!canRequestJoinBackStage) {
      return (
        <DarkTooltip
          placement="top"
          text="Host has temporarily turned off this function."
          width="200px"
        >
          <Button
            style={{
              minWidth: isExtraSmallDevice ? '40vw' : '0',
              marginLeft: '1rem',
              border: '1px solid #d9dbdf',
            }}
            fullWidth={!!isExtraSmallDevice}
            disabled
          >
            Request to Join
          </Button>
        </DarkTooltip>
      );
    }

    if (devicesPermissionState === 'granted') {
      return (
        <Button
          style={{ marginLeft: '1rem', border: '1px solid #d9dbdf' }}
          onClick={() => {
            logEvent(JOIN_EVENT_NAME, attributes);
            processAction(() => {
              onClickRequestToJoinBackstage(isRequestedToJoin ? 'CANCELED' : 'REQUESTED');
            });
          }}
          iconLeft={isSendingJoinRequest ? <FaSpinner /> : ''}
          fullWidth={!!isExtraSmallDevice}
          disabled={!canRequestJoinBackStage}
        >
          {isRequestedToJoin ? 'Cancel Request' : 'Request to Join'}
        </Button>
      );
    }

    return (
      <AdvanceToolTip
        actionButtons={[
          {
            text: 'Tech Check',
            key: 'tech_check',
            method: () => {
              window.location.href = `${origin}/session/${session._id}/live/tech-check`;
            },
            color: 'primary',
            styles: { minWidth: '9rem', marginRight: '1rem' },
          },
        ]}
        width="413px"
        visible={isBrowserCheckTooltipVisible}
        title="Join Stage"
        description="Preparation is everything! Allow browser permissions and check your device. Then your request to join will be sent to the hosts."
        element={
          <Button
            onClick={() => {
              setIsBrowserCheckTooltipVisible(!isBrowserCheckTooltipVisible);
            }}
          >
            Request to Join
          </Button>
        }
      />
    );
  };

  const renderJoinRequestCard =
    liveStatus !== 'NOT_STARTED' &&
    !currentPoster &&
    !isRtmpStream &&
    !(isOrganizer || isChair || isPresenter) &&
    shouldShowVideoPlayer &&
    !isSessionVOD &&
    event?.type !== 'Onsite' &&
    FeatureFlags.Enable_Delegate_To_Stage.isEnabled() ? (
      <SimpleCard
        color="white"
        style={{
          minHeight: 10,
          marginTop: -4,
          cursor: 'unset',
          padding: '24px',
        }}
        className={!isLargeDevice ? '' : 'mb3'}
      >
        <div className="mt1" />
        <Grid container spacing={2}>
          <Grid xs={12} sm={6} item>
            <Presenter user={currentPresenter} eventId={eventId} />
          </Grid>
          <Grid
            xs={12}
            sm={6}
            item
            style={{
              display: 'flex',
              justifyContent: isExtraSmallDevice ? 'unset' : 'flex-end',
              alignItems: 'center',
            }}
          >
            {renderJoinRequestButtons()}
          </Grid>
        </Grid>
      </SimpleCard>
    ) : null;

  return (
    <>
      {FeatureFlags.Enable_Delegate_To_Stage.isEnabled() && (
        <DelegateAcceptModal
          showRequestAcceptModal={showRequestAcceptModal}
          setShowRequestAcceptModal={setShowRequestAcceptModal}
          hostName={hostName}
          onClickDeclineToJoinBackstage={onClickDeclineToJoinBackstage}
          onClickAcceptToJoinBackstage={onClickAcceptToJoinBackstage}
          timeLeftToAccept={timeLeftToAccept}
        />
      )}

      {FeatureFlags.Enable_Delegate_To_Stage.isEnabled() && (
        <DelegateRejectModal
          setShowRejectRequestModal={setShowRejectRequestModal}
          showRejectRequestModal={showRejectRequestModal}
          sessionId={sessionId}
          eventId={eventId}
        />
      )}

      <HeaderSection
        session={session}
        event={event!}
        backStageLink={backStageLink}
        type="PRESENTATIONS"
        isChair={isChair}
        isSpeaker={isPresenter}
        isOrganizer={isOrganizer}
        onBackButtonClick={() => {
          logEvent('BACK_LINK_CLICKED', {
            event_position: 'PRESENTATION_SESSION_PAGE',
          });
          push(`/event/${session.event.id}/sessions`);
        }}
        sessionTime={sessionTime}
        hasRecordings={hasRecordings || shouldShowVideoModeForRtmp || isSessionVOD}
        isEarlyStream={liveStatus === 'EARLY_START' || (liveStatus === 'LIVE' && isPreSession)}
        liveStatus={liveStatus}
        onEditButtonClick={() => {
          logEvent('STANDALONE_CLICKED_EDIT_SESSION');
          window.open(
            `${origin}/conference/event/${session.event.id}/edit-session/${session._id}`,
            '_self',
          );
        }}
        eventId={eventId}
      />

      {FeatureFlags.EventSessionSchedulerExportToCalendar.isEnabled() && (
        <ContainerStyles.SectionContainer
          style={{ paddingTop: '0', paddingBottom: '0', marginTop: '2.5rem' }}
        >
          <Styles.SchedulerSection>
            <ContainerStyles.PageContainer>
              <Flexbox gap="1rem">
                <BookmarkButton
                  session={{ ...session, type: 'PRESENTATIONS' }}
                  toggleBookmark={toggleBookmark}
                  isToggling={isToggling[session._id]}
                  isBookmarked={isBookmarked}
                  showbookmarkButton={canShowBookmarkButton}
                />

                <CalendarExportDropdown session={session} />
              </Flexbox>
            </ContainerStyles.PageContainer>
          </Styles.SchedulerSection>
        </ContainerStyles.SectionContainer>
      )}
      <ContainerStyles.SectionContainer
        style={{
          visibility: shouldHideSessionContainer ? 'hidden' : 'visible',
          height: shouldHideSessionContainer ? '0' : 'auto',
          paddingTop: '0',
          marginTop: FeatureFlags.EventSessionSchedulerExportToCalendar.isEnabled()
            ? '0'
            : '2.5rem',
        }}
      >
        <ContainerStyles.PageContainer withTopMargin>
          <Grid container spacing={4}>
            <Grid xs={12} lg={8} item style={{ height: '100%' }}>
              {event?.type !== 'Onsite' ? (
                <Styles.DragOverlay isDragging={isDragging}>
                  <Styles.FloatVideoContainer ref={floatingElRef} float={floatVideo} top={0}>
                    {floatVideo && (
                      <Styles.DragCover>
                        <small>
                          <FaCompressArrowsAlt
                            size="1rem"
                            onClick={() => {
                              setFloatVideo(false);
                              setDisableFloat(true);
                            }}
                          />
                        </small>
                      </Styles.DragCover>
                    )}
                    <Ratio
                      width={16}
                      height={9}
                      style={{
                        zIndex: 1,
                        position: 'relative',
                        display: shouldShowVideoPlayer ? 'block' : 'none',
                      }}
                    >
                      {renderVideoOrStream()}
                    </Ratio>
                  </Styles.FloatVideoContainer>
                </Styles.DragOverlay>
              ) : null}

              <Ratio width={16} height={floatVideo ? 9 : 0}>
                <div
                  style={{ height: '100%', backgroundColor: '#1F222C' }}
                  ref={floatingVideoRef}
                />
              </Ratio>
              {!shouldShowVideoModeForRtmp &&
                isRtmpStream &&
                !isPreSession &&
                event?.type !== 'Onsite' && (
                  <SessionComments
                    sessionId={session._id}
                    authUser={authUser}
                    canApprove={isOrganizer || isChair}
                    auto_approve_discussions={session.event.auto_approve_discussions}
                    isHappeningNow={isHappeningNow || isMainStageLiveOrPaused}
                  />
                )}
              {((currentPoster && !isRtmpStream) || shouldShowVideoModeForRtmp) &&
                liveStatus !== 'NOT_STARTED' &&
                event?.type !== 'Onsite' && (
                  <SimpleCard
                    color="white"
                    style={{
                      minHeight: 150,
                      marginTop: -4,
                      cursor: 'unset',
                      padding: '24px',
                    }}
                    className={!isLargeDevice ? '' : 'mb3'}
                  >
                    <ContainerStyles.SubTitle>{currentPoster?.title}</ContainerStyles.SubTitle>
                    <div className="mt1" />

                    <Grid container spacing={2}>
                      <Grid xs={12} sm={6} item>
                        <Presenter user={currentPresenter} eventId={eventId} />
                      </Grid>
                      {!hideComments && !!authUser?.id && !!currentPoster && (
                        <Grid
                          xs={12}
                          sm={6}
                          item
                          style={{
                            display: 'flex',
                            justifyContent: isExtraSmallDevice ? 'unset' : 'flex-end',
                            alignItems: 'center',
                          }}
                        >
                          <Button
                            onClick={() => scrollToDiscussions()}
                            fullWidth={!!isExtraSmallDevice}
                          >
                            Ask a question
                          </Button>
                          {FeatureFlags.Enable_Delegate_To_Stage.isEnabled() &&
                            !(isOrganizer || isChair || isPresenter) &&
                            renderJoinRequestButtons()}
                        </Grid>
                      )}
                    </Grid>
                  </SimpleCard>
                )}
              {renderJoinRequestCard}

              {isLargeDevice && event?.type !== 'Onsite' && QnAComponent}
              {isLargeDevice && SpeakersAndSubmissions}
            </Grid>
            <Grid xs={12} lg={4} item>
              {event?.type !== 'Onsite' && shouldShowPlaylist && presentations && (
                <>
                  <Playlist
                    playing={playing}
                    session={props.session}
                    blocked={blockPlaylist}
                    videoSessions={videoSession}
                    posters={presentations.map(poster => ({
                      ...poster,
                      hasEnded: !!getHasEnded(poster.id),
                    }))}
                    isHappeningNow={isHappeningNow}
                    hasRecordings={hasRecordings}
                    liveStatus={liveStatus}
                    shouldShowVideoModeForRtmp={shouldShowVideoModeForRtmp}
                    totalCount={presentationsCount}
                  />
                  {paginatePresentations ? (
                    <div style={{ marginTop: '0.5rem' }}>
                      <PaginationControls
                        currentPage={postersPage}
                        totalPages={totalPages}
                        onPreviousPage={setPostersPage}
                        onNextPage={setPostersPage}
                      />
                    </div>
                  ) : null}
                </>
              )}

              <Tags event={event} session={session} />
            </Grid>
            {!isLargeDevice && (
              <Grid xs={12} lg={8} item>
                {event?.type !== 'Onsite' && QnAComponent}
                {SpeakersAndSubmissions}
              </Grid>
            )}
          </Grid>
        </ContainerStyles.PageContainer>
      </ContainerStyles.SectionContainer>
    </>
  );
};
